import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { ProgramDraftWarning } from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import type { ComposerStyles } from "./composerStyles";
import type { Composer } from "./useComposer";

// The program's PUBLICATION lifecycle: the draft/published/following status
// row and the checklist panel that opens under it (publish, or publish and
// follow). Lifted out of ComposerMetaBand as a pure move — every value it
// reads still arrives as a prop, and the classic meta band renders it in the
// exact place it used to be inline.
//
// ⭐ It was lifted because of WHERE IT HAS TO GO NEXT (PE-39/K5). The prescribe
// editor is about to become the only program editor, and ComposerMetaBand is
// one of the files that dies with the classic shell: leaving the publish
// lifecycle inside it would have meant either killing the only way to publish
// a program, or copying ninety lines into the new tree. The meta band's other
// half (name field, weeks·days line, language chip) is already covered in
// prescribe by PrescribeHeaderFields, so this is the ONLY part that had to
// survive the deletion [[feedback_reuse_nearest_sibling]].
//
// `publishOpen` stays owned by the caller, for the reason ComposerMetaBand
// already documented: ComposerEditLayout renders its `metaBand` on the two
// sides of a `wide ?` ternary, so state held here would be destroyed by a
// breakpoint flip and silently close an open panel.
//
// `warningLabel` arrives as a prop rather than being rebuilt: the warnings
// panel renders the identical list, and a second copy of the emptyCell/
// pctNoMax wording is a divergence waiting to happen.
export function ComposerPublishBand({
  composer,
  cs,
  theme,
  warnings,
  warningLabel,
  publishOpen,
  setPublishOpen,
}: {
  composer: Composer;
  cs: ComposerStyles;
  theme: UiTheme;
  warnings: readonly ProgramDraftWarning[];
  warningLabel: (w: ProgramDraftWarning) => string;
  publishOpen: boolean;
  setPublishOpen: (next: boolean | ((v: boolean) => boolean)) => void;
}) {
  const { t } = useTranslation();
  const p = theme.palette;
  const { draft, busy, save } = composer;

  return (
    <>
      <View style={cs.statusRow}>
        <Text style={cs.metaLine}>
          {draft.status === "draft"
            ? t("program.chipDraft", { defaultValue: "Draft" })
            : draft.status === "archived"
              ? t("program.chipPublished", { defaultValue: "Published" })
              : t("program.chipFollowed", { defaultValue: "Following" })}
        </Text>
        {draft.status === "draft" ? (
          <Pressable
            accessibilityRole="button"
            // ⛔ ONE accessibilityState, both facts: a second prop of the same
            // name silently replaces the first, and the one that would have
            // been lost is the expanded/collapsed state of the panel.
            accessibilityState={{ expanded: publishOpen, disabled: busy }}
            disabled={busy}
            onPress={() => setPublishOpen((v) => !v)}
            style={cs.statusAction}
          >
            <Text style={cs.statusActionText}>
              {t("program.publish", { defaultValue: "Publish" })}…
            </Text>
          </Pressable>
        ) : draft.status === "archived" ? (
          <Pressable
            accessibilityRole="button"
            disabled={busy}
            accessibilityState={{ disabled: busy }}
            onPress={() => void save("draft")}
            style={cs.statusAction}
          >
            <Text style={cs.statusActionText}>
              {t("program.unpublish", { defaultValue: "Back to draft" })}
            </Text>
          </Pressable>
        ) : null}
      </View>

      {publishOpen && draft.status === "draft" ? (
        <View style={cs.publishPanel}>
          {warnings.length > 0 ? (
            <>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <Icon name="warn" size={cs.checkTitle.fontSize} color={p.ink} />
                <Text style={cs.checkTitle}>
                  {t("composer.publishChecklist", {
                    defaultValue: "Open warnings (publishing anyway is fine)",
                  })}
                </Text>
              </View>
              {warnings.map((w, i) => (
                <Text key={i} style={cs.checkItem}>
                  · {warningLabel(w)}
                </Text>
              ))}
            </>
          ) : (
            <Text style={cs.checkItem}>
              {t("composer.publishClean", { defaultValue: "No open warnings." })}
            </Text>
          )}
          <View style={cs.checkActions}>
            <Pressable
              accessibilityRole="button"
              disabled={busy}
              accessibilityState={{ disabled: busy }}
              onPress={() => {
                setPublishOpen(false);
                void save("archived");
              }}
              hitSlop={8}
              style={cs.checkFixBtn}
            >
              <Text style={cs.checkFixText}>
                {t("program.publish", { defaultValue: "Publish" })}
              </Text>
            </Pressable>
            <Pressable
              accessibilityRole="button"
              disabled={busy}
              accessibilityState={{ disabled: busy }}
              onPress={() => {
                setPublishOpen(false);
                void save("active");
              }}
              hitSlop={8}
              style={cs.checkAnywayBtn}
            >
              <Text style={cs.checkAnywayText}>
                {t("program.publishAndFollow", { defaultValue: "Publish and follow" })}
              </Text>
            </Pressable>
          </View>
        </View>
      ) : null}
    </>
  );
}
